import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getLocale, setLocale as setI18nLocale, loadLocale, subscribe, t as translate } from '../i18n';

interface LanguageContextType {
  locale: string;
  setLocale: (lang: string) => void;
  t: (key: string, params?: Record<string, any>) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  locale: 'en',
  setLocale: () => {},
  t: (key) => key,
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState(getLocale());
  const [ready, setReady] = useState(false);

  useEffect(() => {
    loadLocale(getLocale()).then(() => {
      setLocaleState(getLocale());
      setReady(true);
    });
  }, []);

  useEffect(() => {
    return subscribe(() => {
      setLocaleState(getLocale());
    });
  }, []);

  const handleSetLocale = useCallback((lang: string) => {
    setI18nLocale(lang);
  }, []);

  // Set dir attribute for RTL support
  useEffect(() => {
    document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = locale;
  }, [locale]);

  if (!ready) return null;

  return (
    <LanguageContext.Provider value={{ locale, setLocale: handleSetLocale, t: translate }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

export default LanguageContext;
